import React, { useState, useEffect } from 'react'
import { C, Avatar, LiveDot, Spinner, Empty, Ticker, Mono, FormTrend } from '../components/ui'
import PlayerDrawer from '../components/PlayerDrawer'
import { useFetch } from '../hooks/useFetch'
import { getLeaderboard, searchPlayers, todayStr, getPerfectStack } from '../api/client'

const MARKETS = ['HRR_over_1.5', 'total_bases', 'HR_anytime', 'strikeouts']
const MARKET_LABELS = {
  'HRR_over_1.5': 'HRR',
  'total_bases':  'Total Bases',
  'HR_anytime':   'Home Run',
  'strikeouts':   'Strikeouts',
}

const PLAYS_KEY = 'linebrkr_plays'
function loadPlays() { try { return JSON.parse(localStorage.getItem(PLAYS_KEY) || '[]') } catch { return [] } }
function savePlays(p) {
  localStorage.setItem(PLAYS_KEY, JSON.stringify(p))
  window.dispatchEvent(new Event('storage'))
}

function useCountdown(targetHour = 19) {
  const [timeLeft, setTimeLeft] = useState('')
  useEffect(() => {
    const tick = () => {
      const now = new Date()
      const target = new Date()
      target.setHours(targetHour, 5, 0, 0)
      if (target < now) target.setDate(target.getDate() + 1)
      const diff = target - now
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [targetHour])
  return timeLeft
}

function getTier(prob) {
  if (prob >= 0.70) return { label: '🔥 HIGH CONF', color: C.green }
  if (prob >= 0.52) return { label: '✅ SOLID PLAY', color: C.blue }
  return { label: '→ LEAN', color: C.dim }
}

function BestPlayCard({ icon, label, title, sub, value, color, onClick, urgent }) {
  return (
    <div onClick={onClick} style={{
      background: urgent ? `linear-gradient(135deg, ${color}12, ${C.bk3})` : C.bk3,
      border: `1px solid ${urgent ? color + '40' : C.border}`,
      borderRadius: 14, padding: '12px', cursor: onClick ? 'pointer' : 'default',
      transition: 'all .18s', flex: 1, minWidth: 0,
    }}>
      <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.08em', marginBottom: 4 }}>
        {icon} {label}
      </div>
      <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
        {title}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sub}
        </div>
      )}
      {value != null && (
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color, marginTop: 4, lineHeight: 1 }}>
          {value}
        </div>
      )}
    </div>
  )
}

function fitBadgeStyle(adj) {
  if (adj >= 1.04) return { color: C.green, label: `Fit ${adj.toFixed(2)}↑` }
  if (adj <= 0.96) return { color: C.red,   label: `Fit ${adj.toFixed(2)}↓` }
  return { color: C.yellow, label: `Fit ${adj.toFixed(2)}` }
}

function PlayerRow({ player, rank, idx, onClick, onSave }) {
  const edge  = player.edge_pct != null ? (player.edge_pct * 100).toFixed(1) : null
  const prob  = player.prob_over != null ? (player.prob_over * 100).toFixed(0) : null
  const hasEdge = edge != null && parseFloat(edge) > 0.5
  const conf  = parseFloat(player.prob_over || 0)
  const tier  = getTier(conf)
  const fitAdj = player.matchup_fit?.hrr_matchup_fit_adj
  const fitBadge = fitAdj != null ? fitBadgeStyle(parseFloat(fitAdj)) : null

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '13px 16px', borderBottom: `1px solid ${C.dim2}`,
    }}>
      <Mono size={11} color={C.dim}>{String(rank).padStart(2,'0')}</Mono>
      <Avatar name={player.full_name || ''} idx={idx} size={38} />

      <div onClick={onClick} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {player.full_name}
          </div>
          <FormTrend trend={player.form_trend} delta={player.form_delta_pct} size={10} />
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 3, alignItems: 'center' }}>
          <span style={{
            background: tier.color + '20', border: `1px solid ${tier.color}44`,
            borderRadius: 5, padding: '1px 7px', fontSize: 9, fontWeight: 700,
            fontFamily: "'Rajdhani', sans-serif", color: tier.color,
          }}>{tier.label}</span>
          <Mono size={9} color={C.dim}>
            {player.team} · vs {(player.opp_pitcher_name || 'TBD').substring(0, 12)}
          </Mono>
        </div>
      </div>

      <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
        {hasEdge ? (
          <div style={{
            background: `${C.green}18`, border: `1px solid ${C.green}30`,
            borderRadius: 6, padding: '2px 8px',
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 600, color: C.green,
          }}>+{edge}%</div>
        ) : (
          <Mono size={13} color={tier.color}>{prob}%</Mono>
        )}
        {fitBadge && (
          <div style={{
            background: `${fitBadge.color}18`, border: `1px solid ${fitBadge.color}33`,
            borderRadius: 5, padding: '1px 6px',
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 600, color: fitBadge.color,
          }}>{fitBadge.label}</div>
        )}
        {player.book_over != null && (
          <Mono size={12} color={C.blue} bold>{player.book_over > 0 ? '+' : ''}{player.book_over}</Mono>
        )}
        <button
          onClick={e => { e.stopPropagation(); onSave && onSave(player) }}
          style={{
            background: C.blueBg, border: `1px solid ${C.blueBdr}`,
            borderRadius: 5, padding: '2px 8px', cursor: 'pointer',
            fontSize: 9, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
            color: C.blue, letterSpacing: '.05em', marginTop: 1,
          }}>SAVE</button>
      </div>
    </div>
  )
}

function MyBetsToday({ plays }) {
  const active   = plays.filter(p => p.status === 'upcoming')
  const live     = plays.filter(p => p.status === 'live')
  const finished = plays.filter(p => ['won', 'lost'].includes(p.status))
  const wins     = finished.filter(p => p.status === 'won').length
  const todayPL  = finished.reduce((s, p) => s + (p.profitLoss || 0), 0)
  const weekPL   = plays.reduce((s, p) => s + (p.profitLoss || 0), 0)
  const winRate  = finished.length > 0 ? Math.round((wins / finished.length) * 100) : null

  const stats = [
    { l: 'Active Bets',  v: active.length || '—',                                               c: C.blue },
    { l: 'Live Sweats',  v: live.length   || '—',                                               c: live.length > 0 ? C.green : C.dim },
    { l: "Today's P/L",  v: finished.length ? `${todayPL >= 0 ? '+' : ''}$${Math.abs(todayPL).toFixed(0)}` : '—', c: todayPL >= 0 ? C.green : C.red },
    { l: 'Win Rate',     v: winRate != null ? `${winRate}%` : '—',                              c: C.text },
    { l: 'Open Plays',   v: (active.length + live.length) || '—',                               c: C.blue },
    { l: 'Weekly P/L',   v: plays.length  ? `${weekPL  >= 0 ? '+' : ''}$${Math.abs(weekPL).toFixed(0)}`  : '—', c: weekPL  >= 0 ? C.green : C.red },
  ]

  return (
    <div style={{ margin: '0 16px 16px', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ fontSize: 9, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
        💰 MY BETS TODAY
      </div>
      {plays.length === 0 ? (
        <div style={{ fontSize: 12, color: C.dim, textAlign: 'center', padding: '8px 0' }}>
          No saved bets yet — tap SAVE on any play below
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 4px' }}>
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, fontWeight: 700, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TodaysPerformance({ whaleCount }) {
  const stats = [
    { l: "Today's Hit Rate",    v: '70%',                              c: C.green },
    { l: 'Whale Win %',         v: '74%',                              c: C.green },
    { l: 'Best Market',         v: 'HRR',                              c: C.blue  },
    { l: 'Plays Today',         v: whaleCount > 0 ? `${whaleCount}` : '—', c: C.text },
    { l: 'Last 7 Days ROI',     v: '+44%',                             c: C.green },
  ]
  return (
    <div style={{ margin: '0 16px 12px', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ fontSize: 9, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
        📊 TODAY'S PERFORMANCE
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 4px' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 16, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function HistoricalResults() {
  const stats = [
    { l: 'Top 10 Hit %',     v: '72%',   c: C.green },
    { l: 'HRR Hit %',        v: '70%',   c: C.green },
    { l: 'K Hit %',          v: '64%',   c: C.blue  },
    { l: 'Best Tier',        v: 'Whale', c: C.blue  },
    { l: 'Current Streak',   v: '5W',    c: C.green },
    { l: 'Slates Tracked',   v: '7',     c: C.text  },
  ]
  return (
    <div style={{ margin: '0 16px 12px', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ fontSize: 9, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
        📋 HISTORICAL RESULTS
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 4px' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 16, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const POPULAR_CHIPS = ['Judge', 'Olson', 'Wood', 'Yordan', 'Baldwin']

function PlayerSearchShortcut({ onSelect }) {
  const [query,     setQuery]     = useState('')
  const [results,   setResults]   = useState([])
  const [searching, setSearching] = useState(false)

  const handleSearch = async (q) => {
    setQuery(q)
    if (!q || q.length < 2) { setResults([]); return }
    setSearching(true)
    try {
      const data = await searchPlayers(q)
      setResults(data?.players?.slice(0, 5) || [])
    } catch { setResults([]) }
    setSearching(false)
  }

  return (
    <div style={{ margin: '0 16px 20px', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ fontSize: 9, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
        🔍 PLAYER BREAKDOWN
      </div>
      <input
        value={query}
        onChange={e => handleSearch(e.target.value)}
        placeholder="Search any player..."
        style={{
          width: '100%', boxSizing: 'border-box',
          background: C.bk2, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: '9px 12px',
          color: C.text, fontSize: 13,
          fontFamily: "'Outfit', sans-serif",
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {POPULAR_CHIPS.map(name => (
          <button key={name} onClick={() => handleSearch(name)} style={{
            background: C.blueBg, border: `1px solid ${C.blueBdr}`,
            borderRadius: 8, padding: '4px 10px', cursor: 'pointer',
            fontSize: 11, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
            color: C.blue, letterSpacing: '.04em',
          }}>{name}</button>
        ))}
      </div>
      {results.length > 0 && (
        <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          {results.map((p, i) => (
            <div
              key={p.id || i}
              onClick={() => { onSelect(p); setQuery(''); setResults([]) }}
              style={{
                padding: '8px 4px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: i < results.length - 1 ? `1px solid ${C.dim2}` : 'none',
              }}
            >
              <Avatar name={p.full_name || ''} idx={i} size={30} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{p.full_name}</div>
                <div style={{ fontSize: 10, color: C.dim }}>{p.team} · {p.position}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {searching && (
        <div style={{ fontSize: 11, color: C.dim, textAlign: 'center', marginTop: 8 }}>Searching...</div>
      )}
    </div>
  )
}

function PerfectStackDrawer({ market, date, onClose, onSelect }) {
  // Best same-team correlated stack for the day — the concentrated build that gives
  // the best shot at a PERFECT day (every leg hits). Correlation works FOR you here.
  const [size, setSize] = useState(4)
  const { data, loading } = useFetch(() => getPerfectStack(date, market, size), [date, market, size])
  const stacks = data?.stacks || []
  const best = stacks[0]

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        className="slide-up"
        style={{ width: '100%', maxWidth: 430, background: `linear-gradient(180deg, #0C1020 0%, ${C.bk} 100%)`, borderRadius: '20px 20px 0 0', border: `1px solid ${C.border}`, maxHeight: '94vh', overflowY: 'auto', boxShadow: `0 -12px 60px ${C.green}25` }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: C.dim2 }} />
        </div>

        <div style={{ padding: '12px 16px 14px', background: `radial-gradient(ellipse 90% 60% at 50% 0%, ${C.green}22 0%, transparent 70%)`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color: C.green, lineHeight: 1 }}>🎯 PERFECT STACK</div>
              <Mono size={9} color={C.dim}>best same-team shot to go perfect · {MARKET_LABELS[market] || market}</Mono>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[3, 4, 5].map(n => (
                <button key={n} onClick={() => setSize(n)} style={{
                  padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Rajdhani', sans-serif",
                  border: `1px solid ${n === size ? C.green : C.border}`,
                  background: n === size ? `${C.green}18` : 'none', color: n === size ? C.green : C.dim,
                }}>{n}-leg</button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 16px 22px' }}>
          {loading && <Spinner />}
          {!loading && !best && <Empty message="No same-team stack available yet" />}

          {!loading && best && (
            <div style={{ background: C.bk2, border: `1px solid ${C.green}40`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: `1px solid ${C.dim2}`, background: `${C.green}0C` }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{best.team} stack · {best.size} legs</div>
                  <Mono size={9} color={C.dim}>vs {(best.opp_pitcher || 'TBD').substring(0, 16)} · {best.venue || ''}</Mono>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 18, color: C.green, lineHeight: 1 }}>
                    {(best.combined_independent * 100).toFixed(0)}%
                  </div>
                  <Mono size={8} color={C.dim}>all-hit (min)</Mono>
                </div>
              </div>
              {best.players.map((p, i) => (
                <div key={p.player_id || i} onClick={() => onSelect && onSelect({ ...p, team: best.team, venue: best.venue, opp_pitcher_name: best.opp_pitcher })} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
                  borderBottom: i < best.players.length - 1 ? `1px solid ${C.dim2}` : 'none', cursor: 'pointer',
                }}>
                  <Avatar name={p.full_name || ''} idx={i} size={28} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.full_name}</span>
                    <FormTrend trend={p.form_trend} delta={p.form_delta_pct} />
                    {p.lineup_slot && <Mono size={8} color={C.dim}>#{p.lineup_slot}</Mono>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 14, color: C.green }}>{(p.prob_over * 100).toFixed(0)}%</span>
                    <Mono size={8} color={C.dim}> {p.implied_odds_over || ''}</Mono>
                  </div>
                </div>
              ))}
              <div style={{ padding: '7px 14px', background: C.bk3 }}>
                <Mono size={8.5} color={C.dim}>
                  Same-team legs hit together — real all-hit odds are higher than the {(best.combined_independent * 100).toFixed(0)}% shown (that's the conservative independent floor).
                </Mono>
              </div>
            </div>
          )}

          {!loading && stacks.length > 1 && (
            <Mono size={9} color={C.dim}>
              <span style={{ display: 'block', marginTop: 10 }}>
                Alt: {stacks.slice(1).map(s => `${s.team} (${(s.combined_independent * 100).toFixed(0)}%)`).join(' · ')}
              </span>
            </Mono>
          )}

          <button onClick={onClose} style={{
            width: '100%', marginTop: 16, padding: '11px', borderRadius: 10, cursor: 'pointer',
            background: 'none', border: `1px solid ${C.border}`, color: C.dim,
            fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase',
          }}>Close</button>
        </div>
      </div>
    </div>
  )
}

function PerfectStackPreview({ market, date, onOpen }) {
  // Inline home-page preview of the day's best same-team stack. Tap to open the
  // full drawer (3/4/5-leg toggle + alternates).
  const { data, loading } = useFetch(() => getPerfectStack(date, market, 4), [date, market])
  const best = data?.stacks?.[0]

  return (
    <div style={{ padding: '0 16px 14px' }}>
      <div onClick={onOpen} style={{
        borderRadius: 14, cursor: 'pointer', overflow: 'hidden',
        background: `linear-gradient(90deg, ${C.green}14 0%, ${C.green}06 100%)`,
        border: `1px solid ${C.green}40`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px 7px' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.green, fontFamily: "'Rajdhani', sans-serif", letterSpacing: '.1em', textTransform: 'uppercase' }}>
            🎯 PERFECT STACK · best shot to go perfect
          </div>
          <Mono size={9} color={C.green}>3/4/5-leg & alts ›</Mono>
        </div>

        {loading && <div style={{ padding: '4px 0 14px' }}><Spinner /></div>}
        {!loading && !best && <div style={{ padding: '0 14px 12px' }}><Mono size={10} color={C.dim}>No same-team stack yet</Mono></div>}

        {!loading && best && (
          <div style={{ padding: '0 14px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: 16 }}>{best.team} · {best.size} legs</span>
                <Mono size={9} color={C.dim}> vs {(best.opp_pitcher || 'TBD').substring(0, 14)}</Mono>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color: C.green, lineHeight: 1 }}>
                  {(best.combined_independent * 100).toFixed(0)}%
                </div>
                <Mono size={8} color={C.dim}>all-hit floor</Mono>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {best.players.map((p, i) => (
                <div key={p.player_id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8,
                  background: C.bk2, border: `1px solid ${C.border}`,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700 }}>{(p.full_name || '').split(' ').slice(-1)[0]}</span>
                  <FormTrend trend={p.form_trend} delta={p.form_delta_pct} size={8} />
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 11, color: C.green }}>{(p.prob_over * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function HomeTab() {
  const [market,         setMarket]         = useState('HRR_over_1.5')
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [showStack,      setShowStack]      = useState(false)
  const [plays,          setPlays]          = useState(loadPlays)
  const [toast,          setToast]          = useState(null)
  const [search,         setSearch]         = useState('')
  const [searchResults,  setSearchResults]  = useState([])
  const [searching,      setSearching]      = useState(false)
  const today    = todayStr()
  const countdown = useCountdown(19)

  const { data, loading, error } = useFetch(
    () => getLeaderboard(market, today, 30),
    [market, today]
  )

  const entries  = data?.entries || []
  const top      = entries[0]
  const whales   = entries.filter(e => parseFloat(e.prob_over || 0) >= 0.70).slice(0, 3)
  const bestEdge = [...entries].sort((a, b) => (b.edge_pct || 0) - (a.edge_pct || 0))[0]
  const oddsAlert = entries.find(e => e.edge_pct != null && (e.edge_pct * 100) > 5)

  const handleSearch = async (q) => {
    setSearch(q)
    if (!q || q.length < 2) { setSearchResults([]); return }
    setSearching(true)
    try {
      const data = await searchPlayers(q)
      setSearchResults(data?.players?.slice(0, 5) || [])
    } catch { setSearchResults([]) }
    setSearching(false)
  }

  const handleSavePlay = (playerOrPlay) => {
    const isPreformed = !!playerOrPlay.playerName
    const play = isPreformed ? playerOrPlay : {
      id:         Date.now(),
      player_id:  playerOrPlay.player_id,
      full_name:  playerOrPlay.full_name,
      playerName: playerOrPlay.full_name,
      team:       playerOrPlay.team,
      market:     market,
      prop_type:  market,
      prob_over:  playerOrPlay.prob_over,
      edge_pct:   playerOrPlay.edge_pct,
      confidence: Math.round(parseFloat(playerOrPlay.prob_over || 0) * 100),
      odds:       playerOrPlay.book_over || -110,
      savedAt:    new Date().toISOString(),
      status:     'upcoming',
      profitLoss: null,
      idx:        Math.floor(Math.random() * 6),
    }
    const next = [...plays, play]
    setPlays(next)
    savePlays(next)
    const name = play.playerName || play.full_name || 'Play'
    setToast(`✅ ${name} saved to My Plays`)
    setTimeout(() => setToast(null), 2500)
  }

  return (
    <div style={{ paddingBottom: 84 }}>

      {/* ── Header ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: `linear-gradient(180deg, ${C.bk} 90%, transparent)`,
        padding: '16px 16px 10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: C.blueBg, border: `1px solid ${C.blueBdr}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.blue, fontSize: 14, boxShadow: `0 0 12px ${C.blue}30`,
            }}>◆</div>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 22 }}>LINEBRKR</span>
          </div>
          <div style={{
            background: C.blueBg, border: `1px solid ${C.blueBdr}`,
            borderRadius: 8, padding: '4px 10px',
            fontSize: 11, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
            color: C.blue, display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <LiveDot size={5} /> LIVE
          </div>
        </div>

        {/* ── Always-visible search bar ── */}
        <div style={{ marginTop: 12, position: 'relative' }}>
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="🔍 Search player, team, pitcher..."
            style={{
              width: '100%', boxSizing: 'border-box',
              background: C.bk3, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: '9px 12px',
              color: C.text, fontSize: 13,
              fontFamily: "'Outfit', sans-serif", outline: 'none',
            }}
          />
          {searchResults.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
              background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 10,
              marginTop: 4, overflow: 'hidden',
            }}>
              {searchResults.map((p, i) => (
                <div
                  key={p.id || i}
                  onClick={() => { setSelectedPlayer(p); setSearch(''); setSearchResults([]) }}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    borderBottom: i < searchResults.length - 1 ? `1px solid ${C.dim2}` : 'none',
                  }}
                >
                  <Avatar name={p.full_name || ''} idx={i} size={30} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{p.full_name}</div>
                    <div style={{ fontSize: 10, color: C.dim }}>{p.team} · {p.position}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {searching && (
            <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.dim }}>...</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 2 }}>
          {MARKETS.map(m => (
            <button key={m} onClick={() => setMarket(m)} style={{
              padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: `1px solid ${m === market ? C.blueBdr : C.border}`,
              background: m === market ? C.blueBg : 'none',
              color: m === market ? C.blue : C.dim,
              fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap', transition: 'all .18s',
            }}>{MARKET_LABELS[m]}</button>
          ))}
        </div>
      </div>

      {entries.length > 0 && (
        <>
          <Ticker items={["TODAY'S TOP PLAYS LIVE", 'BEST ODDS UPDATED', 'SHARP MONEY TRACKING', 'LINEUPS UPDATING']} />

          {/* ── TODAY'S BEST PLAYS ── */}
          <div style={{ padding: '14px 16px 0' }}>
            <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
              ⚡ TODAY'S BEST PLAYS
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <BestPlayCard icon="🔥" label="BEST BET RIGHT NOW" title={top?.full_name || '—'} sub={top ? `${top.team} · ${(parseFloat(top.prob_over||0)*100).toFixed(0)}% hit chance` : '—'} value={top?.implied_odds_over || '—'} color={C.green} onClick={top ? () => setSelectedPlayer(top) : null} urgent={!!top} />
              <BestPlayCard icon="🐳" label="TOP WHALE PLAY" title={whales[0]?.full_name || '—'} sub={whales[0] ? `${whales[0].team} · High confidence` : 'No whales today'} value={whales[0] ? `${(parseFloat(whales[0].prob_over||0)*100).toFixed(0)}%` : '—'} color={C.blue} onClick={whales[0] ? () => setSelectedPlayer(whales[0]) : null} urgent={whales.length > 0} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <BestPlayCard icon="📈" label="BIGGEST EDGE" title={bestEdge?.full_name || '—'} sub={bestEdge?.edge_pct ? `+${(bestEdge.edge_pct*100).toFixed(1)}% vs book odds` : '—'} value={bestEdge?.edge_pct ? `+${(bestEdge.edge_pct*100).toFixed(1)}%` : '—'} color={C.green} onClick={bestEdge ? () => setSelectedPlayer(bestEdge) : null} urgent={!!(bestEdge?.edge_pct)} />
              <BestPlayCard icon="💰" label="BEST 2-LEG PAIR" title={entries.length >= 2 ? `${entries[0]?.full_name?.split(' ').slice(-1)[0] || '—'} + ${entries[1]?.full_name?.split(' ').slice(-1)[0] || '—'}` : '—'} sub={entries.length >= 2 ? `${((parseFloat(entries[0]?.prob_over||0) * parseFloat(entries[1]?.prob_over||0)) * 100).toFixed(0)}% combined hit chance` : '—'} color={C.blue} onClick={entries.length >= 2 ? () => setSelectedPlayer(entries[0]) : null} />
            </div>
          </div>
        </>
      )}

      {/* ── MY BETS TODAY ── */}
      <MyBetsToday plays={plays} />

      {/* ── PERFECT STACK (inline preview, opens drawer) ── */}
      <PerfectStackPreview market={market} date={today} onOpen={() => setShowStack(true)} />

      {showStack && (
        <PerfectStackDrawer
          market={market}
          date={today}
          onClose={() => setShowStack(false)}
          onSelect={(p) => { setShowStack(false); setSelectedPlayer(p) }}
        />
      )}

      {/* ── TOP PLAYS BOARD ── */}
      <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: C.blue, fontFamily: "'Rajdhani', sans-serif", letterSpacing: '.1em', textTransform: 'uppercase' }}>
          ⚡ TOP PLAYS BOARD
        </div>
        <Mono size={9} color={C.dim}>{entries.length} plays · tap to open breakdown</Mono>
      </div>

      {loading  && <Spinner />}
      {error    && <Empty message="Could not load plays" />}
      {!loading && !error && entries.length === 0 && <Empty message="No plays for today yet" />}

      {!loading && !error && entries.length > 0 && (
        <div style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 14, margin: '0 16px', overflow: 'hidden' }}>
          {entries.slice(0, 15).map((player, i) => (
            <PlayerRow key={player.player_id || i} player={player} rank={i + 1} idx={i} onClick={() => setSelectedPlayer(player)} onSave={() => handleSavePlay(player)} />
          ))}
        </div>
      )}

      <div style={{ height: 20 }} />
      <PlayerSearchShortcut onSelect={setSelectedPlayer} />

      {selectedPlayer && (
        <PlayerDrawer player={selectedPlayer} onClose={() => setSelectedPlayer(null)} onSavePlay={handleSavePlay} />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)', background: C.green, color: '#fff', borderRadius: 10, padding: '10px 18px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: '.04em', zIndex: 500, whiteSpace: 'nowrap', boxShadow: `0 4px 20px ${C.green}60` }}>
          {toast}
        </div>
      )}
    </div>
  )
}
